import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, BOOKING_SCREENING_URL, PHONE_NUMBER, PHONE_TEL } from '../lib/tracking';
import { optimizeImage } from '../lib/image';
import TrustBar from '../components/TrustBar';
import ProblemGrid from '../components/ProblemGrid';
import OutcomeSection from '../components/OutcomeSection';
import ProcessSection from '../components/ProcessSection';
import TestimonialsSection from '../components/TestimonialsSection';
import FaqAccordion from '../components/FaqAccordion';
import SatisfactionGuaranteeModal from '../components/SatisfactionGuaranteeModal';

export default function Home() {
  const [faqs, setFaqs] = useState([]);
  const [isGuaranteeOpen, setIsGuaranteeOpen] = useState(false);

  useEffect(() => {
    // Fetch faqs from CMS
    fetch('https://api.dev.auxo.dk/public/content/faqs')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setFaqs(data.sort((a, b) => (a.sortering || 0) - (b.sortering || 0)));
        }
      })
      .catch((err) => console.error('Error fetching FAQs:', err));
  }, []);

  return (
    <div>
      {/* ─── 1. HERO SECTION: Full-bleed immersive hero with left-aligned overlay ─── */}
      <section className="relative overflow-hidden bg-[#0f141c] min-h-[90vh] flex items-center -mt-[116px] pt-[116px]">
        {/* Full-bleed immersive background image */}
        <img
          src={optimizeImage(
            'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/hero-image.png',
            2000
          )}
          alt="Psykoterapeut Kim Palasz i naturen ved solnedgang"
          className="absolute inset-0 w-full h-full object-cover object-[75%_center] sm:object-[70%_center] lg:object-[68%_center] xl:object-center pointer-events-none"
        />

        {/* Refined gradient overlay ensuring sufficient contrast for text while keeping the hero brighter and lighter */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f141c]/55 via-[#0f141c]/25 sm:via-[#0f141c]/10 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f141c]/25 via-transparent to-black/15 pointer-events-none" />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-32 flex flex-col justify-between min-h-[85vh] lg:min-h-[88vh]">
          {/* Main Content Area (Overlaid on left) */}
          <div className="max-w-2xl text-left pt-6 sm:pt-10">
          {/* Headline over two lines */}
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.12] text-balance drop-shadow-md [text-shadow:_0_2px_14px_rgba(0,0,0,0.75)]">
            Få ro i kroppen, overblik i tankerne <span className="block text-[#ECEEF1]">og kontrol over dit liv</span>
          </h1>

            {/* Constrained-width subheading paragraph */}
            <p className="mt-6 text-base sm:text-lg lg:text-xl text-white/85 leading-relaxed max-w-xl font-normal">
              Kim hjælper mennesker, der kæmper med blandt andet stress, angst, lavt selvværd, traumer, sorg og fastlåste mønstre. Sammen skaber vi et trygt rum, hvor du kan begynde at hele og finde balance.
            </p>

            {/* Primary CTA button group: Pill button + Separate circular icon button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3.5 sm:gap-4 pt-8 sm:pt-10">
              <a
                href={BOOKING_SCREENING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('cta_booking_click', { location: 'hero_primary' })}
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 sm:px-9 sm:py-4.5 rounded-full bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm sm:text-base shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer no-break whitespace-nowrap w-full sm:w-auto text-center"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book en gratis afklaringssamtale</span>
              </a>

              <a
                href="#hjaelp-til"
                title="Se problemområder"
                aria-label="Se hvad du kan få hjælp til"
                className="w-full sm:w-14 h-12 sm:h-14 rounded-full bg-white hover:bg-[#ECEEF1] text-[#5A232E] flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-0.5 shrink-0 cursor-pointer"
              >
                <i className="fa-solid fa-arrow-down text-base" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Bottom Left Stats Row: 3 distinct data points with large numbers, descriptive labels and subtle vertical dividers */}
          <div className="pt-10 sm:pt-14 mt-8 border-t border-white/20 max-w-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/20 text-left gap-4 sm:gap-0">
              {/* Stat 1 */}
              <div className="pb-4 sm:pb-0 pr-0 sm:pr-6">
                <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                  12 år
                </div>
                <div className="text-xs sm:text-sm text-white/75 mt-1 font-medium leading-snug">
                  Klinisk erfaring
                </div>
              </div>

              {/* Stat 2 */}
              <div className="py-4 sm:py-0 px-0 sm:px-6">
                <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                  500+
                </div>
                <div className="text-xs sm:text-sm text-white/75 mt-1 font-medium leading-snug">
                  Klienter hjulpet
                </div>
              </div>

              {/* Stat 3 */}
              <div className="pt-4 sm:pt-0 pl-0 sm:pl-6">
                <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                  100%
                </div>
                <div className="text-xs sm:text-sm text-white/75 mt-1 font-medium leading-snug flex items-center gap-1.5 flex-wrap">
                  <span>Tilfredshedsgaranti</span>
                  <button
                    type="button"
                    onClick={() => setIsGuaranteeOpen(true)}
                    className="text-white/60 hover:text-white underline cursor-pointer text-[11px]"
                    title="Læs vilkår for garantien"
                  >
                    (vilkår)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 2. USP FULL TRUST BAR ─── */}
      <TrustBar />

      {/* ─── 3. PROBLEM BEFORE TREATMENT: "Hvad vil du gerne have hjælp til?" ─── */}
      <div id="hjaelp-til">
        <ProblemGrid />
      </div>

      {/* ─── 4. OUTCOME & DESIRED SITUATION: Reverse EFU ─── */}
      <OutcomeSection />

      {/* ─── 5. SOCIAL PROOF & TESTIMONIALS (Birgitte, Søren, Josefine + Video) ─── */}
      <TestimonialsSection />

      {/* ─── 6. PROCESS: "Sådan foregår det" ─── */}
      <ProcessSection />

      {/* ─── 7. TERAPI OG HYPNOSE OVERSIGT: Metoder & faglighed bagefter ─── */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            <div className="lg:col-span-6 space-y-6 flex flex-col justify-between">
              <div>
                <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
                  Faglige tilgange
                </span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#5A232E] tracking-tight mb-4">
                  Terapi & hypnose tilpasset dine individuelle behov
                </h2>
                <p className="text-base text-[#6c7480] leading-relaxed">
                  Mit arbejde er baseret på en nærværende og anerkendende tilgang, hvor vi sammen finder dine ressourcer og styrker. Jeg tror på, at du allerede har de nødvendige værktøjer i dig – min opgave er at hjælpe dig med at aktivere dem.
                </p>
              </div>

              <div className="space-y-4 py-2">
                <div className="bg-white p-5 rounded-2xl border border-[#D9DDE2] shadow-2xs">
                  <h3 className="font-display text-lg text-[#5A232E] mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-comments text-[#5A232E]" aria-hidden="true" />
                    Psykoterapi (MPF)
                  </h3>
                  <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                    Samtaleterapi, NLP og neuroaffektiv traumeterapi (TBT). Arbejder med at dæmpe stress, bearbejde traumer og sorg, styrke selvværd og skabe balance.
                  </p>
                </div>

                <div className="bg-white p-5 rounded-2xl border border-[#D9DDE2] shadow-2xs">
                  <h3 className="font-display text-lg text-[#5A232E] mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-sparkles text-[#5A232E]" aria-hidden="true" />
                    Hypnoterapi
                  </h3>
                  <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                    Arbejder direkte med underbevidsthedens vaner og overbevisninger. Særligt effektivt ved rygestop, nikotinafhængighed, sukkertrang og dyb afspænding.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/terapi-og-hypnose/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-medium text-sm transition-colors"
                >
                  <span>Læs mere om behandlingsformerne</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/priser/"
                  className="text-sm font-semibold text-[#5A232E] hover:text-[#5A232E] underline transition-colors"
                >
                  Se priser & forløb →
                </Link>
              </div>
            </div>

            {/* Visual consultation room image matching height */}
            <div className="lg:col-span-6 flex flex-col">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-[#D9DDE2] h-full w-full min-h-[380px]">
                <img
                  src={optimizeImage(
                    'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/img-4513.jpg',
                    900
                  )}
                  alt="Kims rolige samtalerum på Marengovej 2 på Amager"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 8. ABOUT KIM SUMMARY: Mennesket bag brandet ─── */}
      <section className="py-20 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col">
              <div className="relative rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-[#D9DDE2] h-full w-full min-h-[400px]">
                <img
                  src={optimizeImage(
                    'kim-portrait-vertical.webp',
                    800
                  )}
                  alt="Psykoterapeut Kim Palasz"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6 flex flex-col justify-between">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block">
                Mød din terapeut
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#5A232E] tracking-tight">
                Kim Palasz
              </h2>
              <div className="text-sm font-semibold uppercase tracking-wider text-[#6c7480]">
                Psykoterapeut MPF · Hypnoterapeut · Traumeterapeut · Supervisor & Coach
              </div>

              <div className="text-sm sm:text-base text-[#11161d] space-y-4 leading-relaxed">
                <p>
                  Siden 2014 har jeg drevet min egen praksis, hvor jeg tilbyder terapi til klienter, der ønsker personlig udvikling, øget trivsel og redskaber til at håndtere livets udfordringer.
                </p>
                <p>
                  Mit arbejde er baseret på en nærværende og anerkendende tilgang, hvor vi sammen finder dine ressourcer og styrker. Min mission er at skabe et trygt rum, hvor du kan vokse, udvikle dig og finde balance i livet.
                </p>
                <p>
                  Jeg er født i 1976 og bor ved Lergravsparken på Amager. Udover min private praksis underviser jeg på psykoterapeutuddannelsen på Dansk NLP Institut.
                </p>
              </div>

              {/* Specialer tags */}
              <div className="pt-2">
                <div className="text-xs font-semibold text-[#5A232E] uppercase tracking-wider mb-2.5">
                  Særlige faglige fokusområder:
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Stress', 'Angst & fobier', 'Lavt selvværd', 'Traumeterapi (TBT)', 'Sorg & kriser', 'Et liv i balance', 'Hypnose'].map((tag, i) => (
                    <span
                      key={i}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg bg-[#ECEEF1] text-[#5A232E] border border-[#D9DDE2]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-kim/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-medium text-sm transition-colors"
                >
                  <span>Læs hele Kims historie & uddannelser</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: 'home_about_cta' })}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm transition-colors no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
                  <span>Book samtale</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 9. PRAKTISK INFORMATION & PRISER COMPACT ─── */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
              Gennemskuelige priser
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
              Priser og praktisk information
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#6c7480]">
              Ingen skjulte gebyrer. Du starter altid med en gratis 20 minutters afklaringssamtale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Terapi Card */}
            <div className="bg-white rounded-2xl p-7 border border-[#D9DDE2] shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-xs font-semibold text-[#5A232E] uppercase tracking-wider">
                  Enkeltsession
                </span>
                <h3 className="font-display text-2xl text-[#5A232E] mt-1 mb-2">Terapi</h3>
                <div className="text-3xl font-display font-bold text-[#5A232E] mb-1">
                  1.200 kr.
                </div>
                <div className="text-xs text-[#6c7480] mb-4">60 minutter pr. session</div>
                <p className="text-xs text-[#6c7480] leading-relaxed">
                  Individuel samtaleterapi og kropslig regulering tilpasset din udfordring.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-[#D9DDE2] text-xs text-[#6c7480]">
                Studerende & pensionister: 800 kr.
              </div>
            </div>

            {/* Klippekort Highlighted Card */}
            <div className="bg-[#5A232E] text-white rounded-2xl p-7 border-2 border-[#5A232E] shadow-xl flex flex-col justify-between relative transform md:-translate-y-2 pt-8">
              {/* Tag half out of box at top, half in box */}
              <div className="absolute -top-3.5 right-6 bg-white text-[#5A232E] text-[11px] font-bold uppercase tracking-wider px-3.5 py-1 rounded-full shadow-md flex items-center gap-1.5 z-10 border border-[#5A232E]/20">
                <span className="w-1.5 h-1.5 rounded-full bg-[#5A232E]" />
                Mest populære forløb
              </div>
              <div>
                <span className="text-xs font-semibold text-[#D9DDE2] uppercase tracking-wider">
                  Klippekort (5 sessioner)
                </span>
                <h3 className="font-display text-2xl text-white mt-1 mb-2">Spar 1.000 kr.</h3>
                <div className="text-3xl font-display font-bold text-white mb-1">
                  5.000 kr.
                </div>
                <div className="text-xs text-[#D9DDE2]/80 mb-4">5 x 60 minutter (1.000 kr./session)</div>
                <p className="text-xs text-white/80 leading-relaxed">
                  Ideelt til et dybdegående forløb med vedvarende forandring og støtte. Gyldigt i 12 måneder.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-white/20 text-xs text-[#D9DDE2]">
                100% tilfredshedsgaranti på 1. session
              </div>
            </div>

            {/* Hypnose Card */}
            <div className="bg-white rounded-2xl p-7 border border-[#D9DDE2] shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-xs font-semibold text-[#5A232E] uppercase tracking-wider">
                  Målrettet pakke
                </span>
                <h3 className="font-display text-2xl text-[#5A232E] mt-1 mb-2">Hypnose</h3>
                <div className="text-3xl font-display font-bold text-[#5A232E] mb-1">
                  3.000 kr.
                </div>
                <div className="text-xs text-[#6c7480] mb-4">2-3 sessioner forløb (inkl. moms)</div>
                <p className="text-xs text-[#6c7480] leading-relaxed">
                  Slip nikotin (cigaretter, snus, vaping) eller dæmp sukkertrang og uønskede vaner.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-[#D9DDE2] text-xs text-[#6c7480]">
                Hurtig og effektiv omkodning
              </div>
            </div>
          </div>

          {/* Payment & Cancellation info */}
          <div className="max-w-3xl mx-auto mt-10 bg-white p-6 rounded-2xl border border-[#D9DDE2] text-xs text-[#6c7480] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <i className="fa-solid fa-credit-card text-[#5A232E] text-base" aria-hidden="true" />
              <span>Betaling: Kort, MobilePay til 113387 eller bankoverførsel</span>
            </div>
            <div className="flex items-center gap-2.5">
              <i className="fa-regular fa-clock text-[#5A232E] text-base" aria-hidden="true" />
              <span>Afbudspolitik: Senest 24 timer før din tid</span>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/priser/"
              className="text-sm font-semibold text-[#5A232E] hover:text-[#5A232E] underline"
            >
              Se alle priser for supervision og coaching →
            </Link>
          </div>
        </div>
      </section>

      {/* ─── 10. GLOBAL FAQ SECTION ─── */}
      <FaqAccordion items={faqs} />

      {/* ─── 11. FINAL DOMINANT CONVERSION CTA SECTION ─── */}
      <section className="py-20 sm:py-28 bg-[#5A232E] text-white relative overflow-hidden border-t border-[#431722]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="text-xs uppercase tracking-widest font-semibold text-white/80 block mb-3">
            Tag første skridt i dag
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
            Klar til at skabe mere ro og overskud i dit liv?
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto">
            Det er helt uforpligtende at tage en snak. Book en gratis afklaringssamtale på 20 minutter, så finder vi ud af, hvordan et forløb kan hjælpe netop dig.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={BOOKING_SCREENING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cta_booking_click', { location: 'final_cta' })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl bg-white hover:bg-[#ECEEF1] text-[#5A232E] font-semibold text-base shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-0.5 no-break whitespace-nowrap cursor-pointer"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book en gratis afklaringssamtale</span>
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs text-white/80">
            <span className="flex items-center gap-1.5">
              <i className="fa-solid fa-check text-emerald-300" aria-hidden="true" />
              Ingen binding
            </span>
            <span className="flex items-center gap-1.5">
              <i className="fa-solid fa-check text-emerald-300" aria-hidden="true" />
              Hurtig opstart
            </span>
            <button
              type="button"
              onClick={() => setIsGuaranteeOpen(true)}
              className="flex items-center gap-1.5 underline decoration-dotted hover:text-white"
            >
              <i className="fa-solid fa-shield-check text-emerald-300" aria-hidden="true" />
              100% tilfredshedsgaranti (læs vilkår)
            </button>
          </div>
        </div>
      </section>

      <SatisfactionGuaranteeModal
        isOpen={isGuaranteeOpen}
        onClose={() => setIsGuaranteeOpen(false)}
      />
    </div>
  );
}
